
import React, { Component, ErrorInfo, ReactNode } from 'react';
import { logError } from '../utils/errorLogger';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: string;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: '',
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: '',
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    const errorInfoStr = errorInfo.componentStack || JSON.stringify(errorInfo);
    
    // Log to our error system
    logError({
      type: 'react',
      message: error.message,
      stack: error.stack || 'No stack trace',
      component: errorInfoStr,
    });
    
    // Update state
    this.setState({
      errorInfo: errorInfoStr,
    });
    
    console.error('React Error Boundary caught an error:', error, errorInfo);
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div className="error-boundary p-4 m-4 border border-red-500 rounded bg-red-50">
          <h2 className="text-xl font-bold text-red-700 mb-2">Something went wrong</h2>
          <p className="mb-2">The application encountered an error. Please try reloading the page.</p>
          <details className="mt-4">
            <summary className="cursor-pointer text-blue-600">Error details</summary>
            <pre className="mt-2 p-2 bg-gray-100 overflow-auto text-sm">
              {this.state.error?.toString() || 'Unknown error'}
            </pre>
            {this.state.errorInfo && (
              <pre className="mt-2 p-2 bg-gray-100 overflow-auto text-sm">
                {this.state.errorInfo}
              </pre>
            )}
          </details>
          <button
            className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            onClick={() => window.location.reload()}
          >
            Reload Page
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}
